import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Loading, Card, EmptyState } from "@/components/common";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Workflow, Plus, ArrowUp, ArrowDown, Trash2, ArrowRight, Users2, Badge as BadgeIcon,
  CheckCircle2, AlertTriangle, Play,
} from "lucide-react";

const inputCls = "h-9 w-full rounded-lg bg-white border border-brand-line px-3 text-sm text-brand-ink outline-none focus:border-brand-civic";
const selCls = "h-9 rounded-lg bg-white border border-brand-line px-2 text-sm text-brand-ink outline-none focus:border-brand-civic";
const primaryBtn = "h-9 px-4 rounded-lg bg-brand-navy text-white font-semibold hover:bg-brand-navy-700 inline-flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50";
const ghostBtn = "h-9 px-3 rounded-lg border border-brand-line text-sm font-semibold text-brand-navy hover:bg-brand-bg inline-flex items-center gap-1.5";

const ASSIGN_OPTIONS = [
  { v: "originating_department_approver", label: "Originating Department Approver" },
  { v: "department_approver", label: "Specific Department Approver" },
  { v: "job_title", label: "Job Title" },
  { v: "specific_user", label: "Specific User" },
];
const sid = () => `s_${Math.random().toString(36).slice(2, 9)}`;

export function AdvancedWorkflowsTab() {
  const [ref, setRef] = useState(null);        // {departments, jobTitles, users}
  const [templates, setTemplates] = useState(null);
  const [editingTid, setEditingTid] = useState(null);

  const loadRef = useCallback(async () => {
    const [d, j, u] = await Promise.all([
      api.get("/departments").then((r) => r.data.departments).catch(() => []),
      api.get("/job-titles?include_inactive=true").then((r) => r.data.job_titles).catch(() => []),
      api.get("/admin/users").then((r) => r.data.users).catch(() => []),
    ]);
    setRef({ departments: d, jobTitles: j, users: u });
  }, []);
  const loadTemplates = useCallback(() =>
    api.get("/workflow-templates?include_inactive=true").then((r) => setTemplates(r.data.workflow_templates)).catch(() => setTemplates(false)), []);

  useEffect(() => { loadRef(); loadTemplates(); }, [loadRef, loadTemplates]);

  if (ref === null || templates === null) return <Loading />;
  if (editingTid) return <TemplateEditor tid={editingTid} ref_={ref} onBack={() => { setEditingTid(null); loadTemplates(); loadRef(); }} />;

  return (
    <div className="space-y-6" data-testid="advanced-workflows-tab">
      <p className="text-sm text-slate-500 max-w-3xl">
        Build true government routing: each step routes independently to a Department Approver,
        Job Title, or specific person. Approve, Return and Deny destinations are configurable.
        Existing items keep the workflow version they started on.
      </p>
      <JobTitlesCard jobTitles={ref.jobTitles} onChange={loadRef} />
      <MembersCard ref_={ref} onChange={loadRef} />
      <TemplatesCard templates={templates} onOpen={setEditingTid} onChange={loadTemplates} />
    </div>
  );
}

/* ---------------- Job Titles ---------------- */
function JobTitlesCard({ jobTitles, onChange }) {
  const [name, setName] = useState("");
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/job-titles", { name: name.trim() }); setName(""); onChange(); toast.success("Job title added"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (jt) => {
    try { await api.put(`/job-titles/${jt.title_id}`, { active: !jt.active }); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Card data-testid="job-titles-card">
      <div className="flex items-center gap-2 mb-3"><BadgeIcon className="h-4 w-4 text-brand-civic" /><h3 className="font-display font-semibold text-brand-ink">Job Titles</h3></div>
      <div className="flex flex-wrap gap-2 mb-3">
        {jobTitles.length === 0 && <span className="text-sm text-slate-400">No job titles yet.</span>}
        {jobTitles.map((jt) => (
          <button key={jt.title_id} onClick={() => toggle(jt)} data-testid={`jobtitle-${jt.title_id}`}
            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${jt.active ? "bg-brand-civic-50 text-brand-civic border-brand-civic/30" : "bg-slate-100 text-slate-400 border-slate-300 line-through"}`}>
            {jt.name}
          </button>
        ))}
      </div>
      <div className="flex gap-2 max-w-md">
        <input data-testid="jobtitle-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. City Manager" className={inputCls} />
        <button data-testid="jobtitle-add" onClick={add} className={primaryBtn}><Plus className="h-4 w-4" /> Add</button>
      </div>
    </Card>
  );
}

/* ---------------- Department members ---------------- */
function MembersCard({ ref_, onChange }) {
  const [dept, setDept] = useState(ref_.departments[0]?.department_id || "");
  const [members, setMembers] = useState([]);
  const [userId, setUserId] = useState("");
  const load = useCallback(() => {
    if (!dept) return;
    api.get(`/departments/${dept}/members`).then((r) => setMembers(r.data.members)).catch(() => setMembers([]));
  }, [dept]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!userId) return;
    try { await api.post(`/departments/${dept}/members`, { user_id: userId, dept_role: "approver" }); setUserId(""); load(); onChange(); toast.success("Approver added"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (m) => {
    try { await api.put(`/department-members/${m.membership_id}`, { active: false }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Card data-testid="dept-members-card">
      <div className="flex items-center gap-2 mb-3"><Users2 className="h-4 w-4 text-brand-civic" /><h3 className="font-display font-semibold text-brand-ink">Department Approvers</h3></div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select data-testid="member-dept-select" value={dept} onChange={(e) => setDept(e.target.value)} className={selCls}>
          {ref_.departments.map((d) => <option key={d.department_id} value={d.department_id}>{d.name}</option>)}
        </select>
      </div>
      <div className="space-y-1.5 mb-3">
        {members.length === 0 && <p className="text-sm text-slate-400">No approvers configured for this department.</p>}
        {members.map((m) => (
          <div key={m.membership_id} data-testid={`member-${m.user_id}`} className="flex items-center gap-2 rounded-lg border border-brand-line px-3 py-1.5 text-sm">
            <span className="font-medium text-brand-ink">{m.user?.name || m.user_id}</span>
            <span className="text-xs text-slate-400">{m.user?.email}</span>
            <span className="ml-auto text-[10px] font-bold uppercase text-brand-civic">{m.dept_role}</span>
            <button data-testid={`member-remove-${m.user_id}`} onClick={() => remove(m)} aria-label="Remove approver" className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2 max-w-lg">
        <select data-testid="member-user-select" value={userId} onChange={(e) => setUserId(e.target.value)} className={`${selCls} flex-1`}>
          <option value="">Select a staff user…</option>
          {ref_.users.filter((u) => u.role !== "viewer").map((u) => <option key={u.user_id} value={u.user_id}>{u.name} · {u.email}</option>)}
        </select>
        <button data-testid="member-add" onClick={add} className={primaryBtn}><Plus className="h-4 w-4" /> Add Approver</button>
      </div>
    </Card>
  );
}

/* ---------------- Templates list ---------------- */
function TemplatesCard({ templates, onOpen, onChange }) {
  const [creating, setCreating] = useState(false);
  return (
    <Card data-testid="wf-templates-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Workflow className="h-4 w-4 text-brand-civic" /><h3 className="font-display font-semibold text-brand-ink">Advanced Workflow Templates</h3></div>
        <button data-testid="create-adv-workflow" onClick={() => setCreating(true)} className={primaryBtn}><Plus className="h-4 w-4" /> Create Workflow</button>
      </div>
      {(!templates || templates.length === 0) ? <p className="text-sm text-slate-400">No advanced workflows yet.</p> : (
        <div className="space-y-2">
          {templates.map((t) => (
            <div key={t.template_id} data-testid={`adv-workflow-${t.template_id}`} className="flex items-center gap-3 rounded-lg border border-brand-line px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-brand-ink text-sm">{t.name}</div>
                <div className="text-xs text-slate-500">
                  {t.active_version ? `Active v${t.active_version}` : "No active version"} · {t.in_flight_count || 0} in flight
                </div>
              </div>
              <button data-testid={`open-adv-workflow-${t.template_id}`} onClick={() => onOpen(t.template_id)} className={ghostBtn}>Open Builder</button>
            </div>
          ))}
        </div>
      )}
      {creating && <CreateTemplateDialog onClose={() => { setCreating(false); onChange(); }} onOpen={onOpen} />}
    </Card>
  );
}

function CreateTemplateDialog({ onClose, onOpen }) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!name.trim() || !code.trim()) { toast.error("Name and code are required."); return; }
    setBusy(true);
    try {
      const it = await api.post("/insight-types", { name: name.trim(), code: code.trim().toUpperCase(), default_visibility: "staff_internal" });
      const t = await api.post("/workflow-templates", { name: name.trim(), insight_type_id: it.data.type_id });
      toast.success("Workflow created — build the steps");
      onClose();
      onOpen(t.data.template_id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-adv-dialog">
        <DialogHeader><DialogTitle>Create Advanced Workflow</DialogTitle>
          <DialogDescription>Creates a new Insight Type and its advanced workflow. New items of this type run on the advanced engine once you activate a version.</DialogDescription></DialogHeader>
        <label className="block"><span className="text-xs font-semibold text-slate-500">Workflow / Insight Type name</span>
          <input data-testid="create-adv-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} /></label>
        <label className="block"><span className="text-xs font-semibold text-slate-500">Short code (e.g. CONTRACT)</span>
          <input data-testid="create-adv-code" value={code} onChange={(e) => setCode(e.target.value)} className={inputCls} /></label>
        <DialogFooter>
          <button onClick={onClose} className={ghostBtn}>Cancel</button>
          <button data-testid="create-adv-submit" disabled={busy} onClick={create} className={primaryBtn}>Create</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Template editor ---------------- */
function TemplateEditor({ tid, ref_, onBack }) {
  const [tmpl, setTmpl] = useState(null);
  const [draft, setDraft] = useState(null);   // draft version object being edited
  const [steps, setSteps] = useState([]);
  const [validation, setValidation] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get(`/workflow-templates/${tid}`);
    setTmpl(r.data);
    let d = r.data.versions.find((v) => v.status === "draft");
    if (!d) d = r.data.versions.find((v) => v.status === "active") || r.data.versions[0];
    setDraft(d);
    setSteps((d?.steps || []).map((s) => ({ ...s })));
  }, [tid]);
  useEffect(() => { load(); }, [load]);

  if (!tmpl || !draft) return <Loading />;
  const readOnly = draft.status !== "draft";
  const finalId = steps.find((s) => s.step_type === "finalization")?.step_id;
  const stepOptions = steps.map((s) => ({ v: s.step_id, label: s.name }));

  const setStep = (i, patch) => setSteps(steps.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const move = (i, d) => {
    const j = i + d; if (j < 0 || j >= steps.length) return;
    const c = [...steps]; [c[i], c[j]] = [c[j], c[i]]; setSteps(c);
  };
  const remove = (i) => setSteps(steps.filter((_, idx) => idx !== i));
  const addStep = () => {
    const finIdx = steps.findIndex((s) => s.step_type === "finalization");
    const ns = { step_id: sid(), name: "New Review", step_type: "approval", assignment_type: "department_approver",
      department_id: ref_.departments[0]?.department_id || "", job_title_id: "", specific_user_id: "",
      approve_target: finId(), return_target: "__creator__", sequence: steps.length };
    const copy = [...steps]; copy.splice(finIdx < 0 ? steps.length : finIdx, 0, ns); setSteps(copy);
  };
  function finId() { return steps.find((s) => s.step_type === "finalization")?.step_id; }

  const save = async () => {
    setBusy(true);
    try {
      const payload = { steps: steps.map((s, i) => ({ ...s, sequence: i })) };
      const r = await api.put(`/workflow-versions/${draft.version_id}/steps`, payload);
      setValidation(r.data.validation);
      if (r.data.validation.valid) toast.success("Saved — workflow is valid");
      else toast.warning("Saved as draft — resolve validation issues before activating");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const activate = async () => {
    setBusy(true);
    try {
      await api.put(`/workflow-versions/${draft.version_id}/steps`, { steps: steps.map((s, i) => ({ ...s, sequence: i })) });
      await api.post(`/workflow-versions/${draft.version_id}/activate`);
      toast.success(`Activated v${draft.version}`);
      onBack();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.errors) { setValidation({ valid: false, errors: d.errors }); toast.error("Cannot activate — see issues"); }
      else toast.error(formatApiError(d));
    } finally { setBusy(false); }
  };
  const newDraft = async () => {
    try { const r = await api.post(`/workflow-templates/${tid}/versions`); await load(); toast.success(`Draft v${r.data.version} created`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="adv-workflow-editor">
      <button onClick={onBack} className="text-sm text-brand-civic hover:underline mb-3">← Back to workflows</button>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div><h3 className="font-display font-bold text-brand-ink text-lg">{tmpl.name}</h3>
          <p className="text-xs text-slate-500">Editing v{draft.version} · <span className="font-semibold uppercase">{draft.status}</span></p></div>
        <div className="flex gap-2">
          {readOnly && <button data-testid="wf-new-draft" onClick={newDraft} className={ghostBtn}><Plus className="h-4 w-4" /> New Draft Version</button>}
          {!readOnly && <button data-testid="wf-save-steps" onClick={save} disabled={busy} className={ghostBtn}>Save Draft</button>}
          {!readOnly && <button data-testid="wf-activate" onClick={activate} disabled={busy} className={primaryBtn}><Play className="h-4 w-4" /> Activate</button>}
        </div>
      </div>

      {validation && !validation.valid && (
        <div data-testid="wf-validation-errors" className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          <div className="flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-4 w-4" /> Resolve before activating</div>
          <ul className="list-disc ml-5 mt-1">{validation.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}
      {validation && validation.valid && (
        <div className="mb-3 rounded-lg border border-brand-teal/40 bg-brand-teal-50 p-2.5 text-sm text-brand-teal flex items-center gap-1.5" data-testid="wf-valid"><CheckCircle2 className="h-4 w-4" /> Workflow is valid.</div>
      )}

      <ol className="space-y-2" data-testid="wf-step-list">
        {steps.map((s, i) => (
          <li key={s.step_id} data-testid={`wf-step-${s.step_id}`}>
            <StepCard s={s} i={i} count={steps.length} ref_={ref_} stepOptions={stepOptions} finalId={finalId}
              readOnly={readOnly} setStep={setStep} move={move} remove={remove} />
          </li>
        ))}
      </ol>
      {!readOnly && (
        <button data-testid="wf-add-step" onClick={addStep} className={`${ghostBtn} mt-3`}><Plus className="h-4 w-4" /> Add Step</button>
      )}

      <Card className="mt-4"><PreviewInline steps={steps} /></Card>
    </div>
  );
}

function StepCard({ s, i, count, ref_, stepOptions, finalId, readOnly, setStep, move, remove }) {
  const isCreator = s.step_type === "creator";
  const isFinal = s.step_type === "finalization";
  const others = stepOptions.filter((o) => o.v !== s.step_id);
  return (
    <div className="rounded-lg border border-brand-line bg-white p-3">
      <div className="flex items-center gap-2">
        <span className="text-xs font-mono text-slate-400 w-5">{i + 1}</span>
        {isCreator || isFinal ? (
          <span className="font-semibold text-brand-ink text-sm flex-1">{s.name} <span className="text-[10px] uppercase text-slate-400">{s.step_type}</span></span>
        ) : (
          <input data-testid={`step-name-${s.step_id}`} disabled={readOnly} value={s.name} onChange={(e) => setStep(i, { name: e.target.value })}
            className="flex-1 h-8 rounded border border-brand-line px-2 text-sm font-semibold outline-none focus:border-brand-civic" />
        )}
        {!readOnly && !isCreator && !isFinal && (
          <>
            <button onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move ${s.name} up`} data-testid={`step-up-${s.step_id}`} className="p-1 text-slate-400 hover:text-brand-navy disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
            <button onClick={() => move(i, 1)} disabled={i >= count - 1} aria-label={`Move ${s.name} down`} data-testid={`step-down-${s.step_id}`} className="p-1 text-slate-400 hover:text-brand-navy disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
            <button onClick={() => remove(i)} aria-label={`Remove ${s.name}`} data-testid={`step-remove-${s.step_id}`} className="p-1 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
          </>
        )}
      </div>

      {!isFinal && (
        <div className="grid sm:grid-cols-2 gap-2 mt-3">
          {!isCreator && (
            <label className="text-xs text-slate-500">Who handles this step?
              <select data-testid={`step-assign-${s.step_id}`} disabled={readOnly} value={s.assignment_type || ""} onChange={(e) => setStep(i, { assignment_type: e.target.value })} className={`${selCls} w-full mt-0.5`}>
                {ASSIGN_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </label>
          )}
          {s.assignment_type === "department_approver" && (
            <label className="text-xs text-slate-500">Department
              <select data-testid={`step-dept-${s.step_id}`} disabled={readOnly} value={s.department_id || ""} onChange={(e) => setStep(i, { department_id: e.target.value })} className={`${selCls} w-full mt-0.5`}>
                <option value="">Select…</option>
                {ref_.departments.map((d) => <option key={d.department_id} value={d.department_id}>{d.name}</option>)}
              </select>
            </label>
          )}
          {(s.assignment_type === "job_title" || s.assignment_type === "department_approver") && (
            <label className="text-xs text-slate-500">{s.assignment_type === "job_title" ? "Job Title" : "Job Title (optional)"}
              <select data-testid={`step-title-${s.step_id}`} disabled={readOnly} value={s.job_title_id || ""} onChange={(e) => setStep(i, { job_title_id: e.target.value })} className={`${selCls} w-full mt-0.5`}>
                <option value="">{s.assignment_type === "job_title" ? "Select…" : "Any"}</option>
                {ref_.jobTitles.filter((j) => j.active).map((j) => <option key={j.title_id} value={j.title_id}>{j.name}</option>)}
              </select>
            </label>
          )}
          {s.assignment_type === "specific_user" && (
            <label className="text-xs text-slate-500">User
              <select data-testid={`step-user-${s.step_id}`} disabled={readOnly} value={s.specific_user_id || ""} onChange={(e) => setStep(i, { specific_user_id: e.target.value })} className={`${selCls} w-full mt-0.5`}>
                <option value="">Select…</option>
                {ref_.users.filter((u) => u.role !== "viewer").map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
              </select>
            </label>
          )}
          <label className="text-xs text-slate-500">Approve → Next step
            <select data-testid={`step-approve-${s.step_id}`} disabled={readOnly} value={s.approve_target || ""} onChange={(e) => setStep(i, { approve_target: e.target.value })} className={`${selCls} w-full mt-0.5`}>
              <option value="">Select…</option>
              {others.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </label>
          {!isCreator && (
            <label className="text-xs text-slate-500">Return → Destination
              <select data-testid={`step-return-${s.step_id}`} disabled={readOnly} value={s.return_target || ""} onChange={(e) => setStep(i, { return_target: e.target.value })} className={`${selCls} w-full mt-0.5`}>
                <option value="__creator__">Creator</option>
                <option value="__previous__">Previous step</option>
                {others.filter((o) => o.v !== finalId).map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </label>
          )}
          {!isCreator && <div className="text-xs text-slate-400 self-end">Deny → <span className="font-medium">End as Denied</span></div>}
        </div>
      )}
    </div>
  );
}

function PreviewInline({ steps }) {
  const byId = Object.fromEntries(steps.map((s) => [s.step_id, s]));
  const creator = steps.find((s) => s.step_type === "creator");
  const path = []; const seen = new Set(); let cur = creator;
  while (cur && !seen.has(cur.step_id)) { seen.add(cur.step_id); path.push(cur); if (cur.step_type === "finalization") break; cur = byId[cur.approve_target]; }
  return (
    <div data-testid="adv-workflow-preview">
      <h4 className="font-display font-semibold text-brand-ink text-sm mb-2">Preview</h4>
      <div className="flex flex-wrap items-center gap-1.5">
        {path.map((s, i) => (
          <React.Fragment key={s.step_id}>
            <span className="inline-flex flex-col rounded-md bg-brand-bg border border-brand-line px-2 py-1 text-xs">
              <span className="font-medium text-brand-ink">{s.name}</span>
            </span>
            {i < path.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-slate-300" aria-hidden="true" />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
